import { AnimatePresence, motion } from 'framer-motion'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useDxtradeAccountMetricsWS } from '@pages/Trade/Portfolio/context/DxtradeAccountMetricsContext'
import { formatSignedUsd, getPnlColor } from '@pages/Trade/Portfolio/utils/formatters'
import { UsedMarginTooltipNew } from '@pages/Trade/TradingAccountInfo/components/UsedMarginTooltipNew'
import { useFundAccount } from '@pages/Trade/TradingAccountInfo/hooks/useFundAccount'
import { Account } from '@pages/Trade/types'
import { mapAccountMetrics } from '@pages/Trade/utils/helpers'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Truncate } from '@ui/Truncate/Truncate'
import { Body2, Caption1, Heading6, Title1 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Badge } from '@ui/components/Badge/Badge'
import { Tooltip } from '@ui/components/Tooltip'
import { TextColorType } from '@ui/design-system/colors/colors'
import { CaretIcon } from '@ui/icons/Caret.icon'
import { InfoIcon } from '@ui/icons/Info.icon'
import { WarningIcon } from '@ui/icons/Warning.icon'

import { useWindowResize } from '@utils/domUtils'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'

import styles from './SelectedTradingAccount.module.scss'

const TABLET_BREAKPOINT = 1082

type SelectedTradingAccountProps = {
  account: Account
}
export type SelectedTradingAccountPropertyProps = {
  title: string
  tooltip?: React.ReactNode
  accountMetricsValue: string | React.ReactNode
  color?: TextColorType
  onClick?: () => void
}

export const SelectedTradingAccount = ({ account }: SelectedTradingAccountProps) => {
  const [expanded, setExpanded] = useState(false)
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth > TABLET_BREAKPOINT)
  const { formatMoney, formatNumber } = useFormatNumber()
  const isMobile = useWindowResize()
  const { t } = useTranslation()

  const handleAddFunds = useFundAccount(account)

  const lowMarginBadge = (
    <Tooltip
      heading={t('Markets.Low Margin Level Title')}
      description={t('Markets.Low Margin Level Description')}
      primaryButton={{
        label: t('Add funds'),
        onClick: handleAddFunds,
      }}
    >
      <Badge size='sm' type='yellow' contrast='low' leftIcon={WarningIcon}>
        {t('Markets.Low Margin Level')}
      </Badge>
    </Tooltip>
  )

  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth > TABLET_BREAKPOINT
      setIsDesktop(desktop)
      if (!desktop) {
        setExpanded(false)
      }
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const wsAccountKey = `default:${account.platformOverview.login}`
  const wsMetrics = useDxtradeAccountMetricsWS(wsAccountKey)

  const metrics = mapAccountMetrics({
    ws: wsMetrics,
    rest: account.platformOverview,
  })
  const pnlColor = getPnlColor(metrics.openPL)
  const pnlFormatted =
    metrics.openPL !== null ? formatSignedUsd(metrics.openPL, account.currency.id) : null
  const isLowMargin = metrics.marginLevel !== null && metrics.marginLevel < 100

  const properties: SelectedTradingAccountPropertyProps[] = [
    {
      title: t('Markets.Balance'),
      tooltip: t('Markets.BalanceTooltip'),
      accountMetricsValue:
        metrics.balance !== null ? formatMoney(metrics.balance, account.currency.id) : '—',
    },
    {
      title: t('Markets.PnL'),
      tooltip: t('Markets.PnLTooltip'),
      accountMetricsValue: pnlFormatted ?? '—',
      color: pnlColor,
    },
    {
      title: t('Markets.Equity'),
      tooltip: t('Markets.EquityTooltip'),
      accountMetricsValue:
        metrics.equity !== null ? formatMoney(metrics.equity, account.currency.id) : '—',
      color: isLowMargin ? 'text-danger-high-em' : undefined,
    },
    {
      title: t('Markets.FreeMargin'),
      tooltip: t('Markets.FreeMarginTooltip'),
      accountMetricsValue:
        metrics.marginFree !== null ? formatMoney(metrics.marginFree, account.currency.id) : '—',
    },
    {
      title: t('Markets.UsedMargin'),
      tooltip:
        metrics.marginLevel !== null ? <UsedMarginTooltipNew /> : t('Markets.UsedMarginTooltip'),
      accountMetricsValue:
        metrics.usedMargin !== null ? (
          metrics.marginLevel !== null ? (
            <>
              {formatMoney(metrics.usedMargin, account.currency.id)}{' '}
              <Caption1 medium color={isLowMargin ? 'text-danger-high-em' : 'text-high-em'}>
                ({formatNumber(metrics.marginLevel, { minDecimalPlaces: 0, maxDecimalPlaces: 0 })}%)
              </Caption1>
            </>
          ) : (
            formatMoney(metrics.usedMargin, account.currency.id)
          )
        ) : (
          '—'
        ),
    },
  ]
  return (
    <AnimatePresence mode='wait' initial={false}>
      {!expanded ? (
        <motion.div
          key='collapsed'
          className={styles.notExpandedContainer}
          initial={{ opacity: 0, x: -2 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -2 }}
          transition={{ duration: 0.1, ease: [0.4, 0, 0.2, 1] }}
        >
          {isMobile ? (
            <Heading6 medium>
              {metrics.balance !== null ? (
                <SensitiveData>{formatMoney(metrics.balance, account.currency.id)}</SensitiveData>
              ) : (
                ''
              )}
            </Heading6>
          ) : (
            <Title1 medium>
              {metrics.balance !== null ? (
                <SensitiveData>{formatMoney(metrics.balance, account.currency.id)}</SensitiveData>
              ) : (
                ''
              )}
            </Title1>
          )}

          <Body2 medium color={pnlColor}>
            {pnlFormatted && <SensitiveData>{pnlFormatted}</SensitiveData>}
          </Body2>

          {metrics.balance !== null && !isMobile && (
            <Box flex align='center'>
              {isDesktop && <ActionIcon icon={CaretIcon.Right} onClick={() => setExpanded(true)} />}
              {isLowMargin && lowMarginBadge}
            </Box>
          )}
        </motion.div>
      ) : (
        <motion.div
          key='expanded'
          className={styles.expandedContainer}
          initial={{ opacity: 0, x: 4 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 4 }}
          transition={{ duration: 0.1, ease: [0.4, 0, 0.2, 1] }}
        >
          {properties.map((property, i) => (
            <div className={styles.selectedAccountWrapper}>
              <SelectedTradingAccountProperty
                key={i}
                title={property.title}
                tooltip={property.tooltip}
                accountMetricsValue={property.accountMetricsValue}
                color={property.color}
                onClick={property.onClick}
              />
            </div>
          ))}
          <Box flex align='center' gap='x8'>
            <ActionIcon icon={CaretIcon.Left} onClick={() => setExpanded(false)} />
            {isLowMargin && lowMarginBadge}
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export const SelectedTradingAccountProperty = ({
  title,
  tooltip,
  accountMetricsValue,
  color,
}: SelectedTradingAccountPropertyProps) => {
  const isMobile = useWindowResize()
  return (
    <div className={styles.selectedAccountPropertyContainer}>
      <div className={styles.nameIconContainer}>
        <Caption1 className={styles.nameIconText} color={'text-med-em'}>
          <Truncate>{title}</Truncate>
        </Caption1>

        <Tooltip
          heading={isMobile ? title : undefined}
          content={tooltip}
          direction='left'
          wrapperClassName={styles.nameIconTooltip}
        >
          <Box flex onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
            <InfoIcon size={16} />
          </Box>
        </Tooltip>
      </div>

      <Caption1 medium color={color ?? 'text-high-em'}>
        <SensitiveData>{accountMetricsValue}</SensitiveData>
      </Caption1>
    </div>
  )
}
